// Collections ("my own course") — a user-curated ordered list of note references spanning any
// notebook. Plain client-side Firestore CRUD (covered by the standard owner read/write rule in
// firestore.rules — no AI-generated/untrusted fields here, unlike videoIdeas, so no Cloud
// Function indirection is needed).
import { getFirestore, collection, doc, getDocs, setDoc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { Collection, CollectionItem } from '../types';

const colRef = (uid: string) => collection(getFirestore(), `users/${uid}/collections`);

const fromSnap = (snap: any): Collection[] =>
    snap.docs.map((d: any) => ({ id: d.id, ...(d.data() as any) } as Collection));

export const subscribeCollections = (uid: string, cb: (cols: Collection[]) => void): (() => void) => {
    return onSnapshot(colRef(uid), snap => cb(fromSnap(snap)));
};

export const getCollectionsOnce = async (uid: string): Promise<Collection[]> => {
    const snap = await getDocs(colRef(uid));
    return fromSnap(snap);
};

export const createCollection = async (uid: string, title: string): Promise<string> => {
    const ref = doc(colRef(uid));
    const now = Date.now();
    await setDoc(ref, { title: title.trim() || 'Untitled collection', description: '', items: [], createdAt: now, updatedAt: now });
    return ref.id;
};

// Call on commit (blur/Enter), never per keystroke — the trim would eat a trailing space mid-typing.
export const renameCollection = async (uid: string, id: string, title: string): Promise<void> => {
    await updateDoc(doc(colRef(uid), id), { title: title.trim() || 'Untitled collection', updatedAt: Date.now() });
};

export const updateCollectionDescription = async (uid: string, id: string, description: string): Promise<void> => {
    await updateDoc(doc(colRef(uid), id), { description, updatedAt: Date.now() });
};

export const setCollectionPublic = async (uid: string, id: string, isPublic: boolean): Promise<void> => {
    await updateDoc(doc(colRef(uid), id), { isPublic, updatedAt: Date.now() });
};

export const deleteCollection = async (uid: string, id: string): Promise<void> => {
    await deleteDoc(doc(colRef(uid), id));
};

// Returns false (no-op) if this exact node is already in the collection — callers should surface
// that as "already added" rather than silently duplicating.
export const addItemToCollection = async (
    uid: string, collectionId: string, existingItems: CollectionItem[],
    item: Omit<CollectionItem, 'order'>,
): Promise<boolean> => {
    if (existingItems.some(i => i.nodeId === item.nodeId && i.notebookId === item.notebookId)) return false;
    const newItems: CollectionItem[] = [...existingItems, { ...item, order: existingItems.length }];
    await updateDoc(doc(colRef(uid), collectionId), { items: newItems, updatedAt: Date.now() });
    return true;
};

export const removeItemFromCollection = async (
    uid: string, collectionId: string, items: CollectionItem[], nodeId: string, notebookId: string,
): Promise<void> => {
    const filtered = items
        .filter(i => !(i.nodeId === nodeId && i.notebookId === notebookId))
        .map((it, idx) => ({ ...it, order: idx }));
    await updateDoc(doc(colRef(uid), collectionId), { items: filtered, updatedAt: Date.now() });
};

// Persists a full reordering — caller passes the items array in its new desired order.
export const reorderCollectionItems = async (uid: string, collectionId: string, items: CollectionItem[]): Promise<void> => {
    const reindexed = items.map((it, idx) => ({ ...it, order: idx }));
    await updateDoc(doc(colRef(uid), collectionId), { items: reindexed, updatedAt: Date.now() });
};
